import {DataStatus, Form, FormSubmitHandler} from 'platform/components';
import {useFormatCurrency} from 'platform/locale';

import {RefObject} from 'react';

import {includes, isEmpty, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetOperationItemsQuery} from '@dms/api/finance';
import {
  useGetLabourRatesListQuery,
  useGetMetadaServiceCaseQuery,
  useGetServiceOrderJobItemWorkQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
  usePatchOrderItemForWorkMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  getInvoiceName,
  getPercentageFromDecimal,
  handleApiError,
  usePermissions,
  useTenant,
} from '@dms/shared';
import {isNonEditableServiceOrderState} from '@dms/shared-aftersales';

import {TestIdProps} from 'shared/web';

import {getPatchOrderItemWorkBody} from '../../../utils/getPatchOrderItemWorkBody';
import {getRatioFromAmount} from '../../../utils/getRatioFromAmount';
import {isMechanicsRatioEqualToTotalRatio} from '../../../utils/isMechanicsRatioEqualToTotalRatio';
import {editLabourItemFormSchema, EditLabourItemFormType} from '../types/editLabourItemFormSchema';
import {EditLabourItemFormContent} from './EditLabourItemFormContent';

const TOTAL_RATIO = 100;
const DIRECT_PRICE_DEFAULT_QUANTITY = 1;
const MANUAL_DISCOUNT_SOURCE_TYPE = 'manual-discount';

interface EditLabourItemProps extends TestIdProps {
  itemId: string;
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  onClose: VoidFunction;
  isSavedByRowControlRef?: RefObject<boolean>;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function EditLabourItemForm(props: EditLabourItemProps) {
  const tenant = useTenant();
  const formatPrice = useFormatCurrency();

  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});
  const {
    data: itemData,
    isLoading: isItemLoading,
    isFetching: isItemFetching,
    isError: isItemError,
  } = useGetServiceOrderJobItemWorkQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceItemId: props.itemId,
      serviceJobId: props.serviceJobId,
      serviceOrderId: props.serviceOrderId,
    },
    {refetchOnMountOrArgChange: true}
  );

  const {
    data: labourRates,
    options: labourRatesOptions,
    isLoading: isLabourRatesLoading,
    isError: isLabourRatesError,
  } = useGetLabourRatesListQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {
      selectFromResult: (data) => ({
        ...data,
        options: data?.data?.map((labourRate) => ({
          label: `${formatPrice(
            labourRate.rateValue.withoutVat.amount ?? 0,
            labourRate.rateValue.withoutVat.currency ?? tenant.tenantCurrency
          )} - ${labourRate.name}`,
          value: labourRate.labourRateId,
        })),
      }),
    }
  );

  const {
    options: workItemGroupOptions,
    isLoading: isWorkItemGroupLoading,
    isError: isWorkItemGroupError,
  } = useGetOperationItemsQuery(
    {application: 'workshop', itemGroup: 'work'},
    {
      selectFromResult: (data) => ({
        ...data,
        options:
          data?.data?.map((item) => ({label: item.itemCodeName, value: item.operationId})) ?? [],
      }),
    }
  );

  const {data: serviceOrder} = useGetServiceOrderQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {data: serviceOrderVariant} = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: String(serviceOrder?.serviceOrderVariantId ?? '')},
    {skip: isNil(serviceOrder?.serviceOrderVariantId)}
  );

  const [patchOrderItemForWork, {isLoading: isPatchLoading}] = usePatchOrderItemForWorkMutation({
    fixedCacheKey: 'edit-service-order-work-item',
  });

  const isOrderReadOnly =
    isNonEditableServiceOrderState(serviceOrder?.state) ||
    includes(serviceCase?.state, ['CANCELLED']);

  const permissionScope = {
    branchId: serviceCase?.branchId ?? '',
    serviceCaseType: serviceOrderVariant?.serviceCaseTypeId ?? '',
    serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
  };

  const [canEditCustomPrice, canEditManualDiscount, canReadServiceOrderFinancesLabourItem] =
    usePermissions({
      permissionKeys: [
        'serviceItemLabourCustomPriceEdit',
        'serviceItemLabourManualDiscountEdit',
        'serviceOrderFinancesLabourItemRead',
      ],
      scopes: {
        serviceItemLabourCustomPriceEdit: permissionScope,
        serviceItemLabourManualDiscountEdit: permissionScope,
        serviceOrderFinancesLabourItemRead: permissionScope,
      },
    });

  const handleSubmit: FormSubmitHandler<EditLabourItemFormType> = async (data, setErrors) => {
    if (!isMechanicsRatioEqualToTotalRatio(data.assignMechanics)) {
      return setErrors(
        (data.assignMechanics ?? []).map((_, index) => ({
          name: `assignMechanics.${index}.ratio`,
          message: i18n.t('entity.order.labels.ratioSumError'),
        }))
      );
    }

    await patchOrderItemForWork({
      serviceCaseId: props.serviceCaseId,
      serviceItemId: props.itemId,
      serviceJobId: props.serviceJobId,
      serviceOrderId: props.serviceOrderId,
      body: getPatchOrderItemWorkBody(data, itemData),
    })
      .unwrap()
      .then(() => {
        // Saving changes by rowControl change should not close dialog.
        if (props.isSavedByRowControlRef?.current) {
          props.isSavedByRowControlRef.current = false;
          return;
        }
        props.onClose();
      })
      .catch(handleApiError);
  };

  const quantityAmount = itemData?.quantity?.amount;
  const sellingPricePerUnitAmount = itemData?.sellingPricePerUnit?.amount;
  const hasSingleMechanic = itemData?.assignMechanics?.length === 1;

  const selectedDiscount = itemData?.discount?.availableDiscounts?.find(
    (discount) => discount?.isSelected
  );
  const manualDiscount = itemData?.discount?.availableDiscounts?.find(
    (discount) => discount?.sourceType === MANUAL_DISCOUNT_SOURCE_TYPE
  );

  const sellingPrice =
    isNotNil(sellingPricePerUnitAmount) && isNotNil(quantityAmount)
      ? sellingPricePerUnitAmount * quantityAmount
      : null;

  const mappedMechanics =
    itemData?.assignMechanics?.filter(isNotNil)?.map((mechanic) => ({
      id: mechanic.id ?? null,
      amount: hasSingleMechanic ? (quantityAmount ?? 1) : (mechanic?.amount ?? 0),
      ratio: hasSingleMechanic
        ? TOTAL_RATIO
        : (getPercentageFromDecimal(mechanic.ratio) ??
          getRatioFromAmount(mechanic.amount, quantityAmount) ??
          0),
    })) ?? [];

  // Fall back to a single default mechanic row when the API returns no mechanics
  // (including an empty array), so the ratio validation receives a 100% row instead
  // of silently blocking submission with a 0% sum.
  const assignMechanics = isEmpty(mappedMechanics)
    ? [{id: null, amount: quantityAmount ?? 1, ratio: TOTAL_RATIO}]
    : mappedMechanics;

  const defaultValues: EditLabourItemFormType = {
    sNo: null,
    labourRateId: itemData?.labourRateId ?? '',
    name: getInvoiceName({name: itemData?.name, nameForInvoice: itemData?.nameForInvoice}),
    number: itemData?.number,
    workType: itemData?.workType,
    priceType: itemData?.priceType,
    quantity:
      itemData?.priceType === 'SVCPRICETYPE_DIRECT_PRICE'
        ? DIRECT_PRICE_DEFAULT_QUANTITY
        : (quantityAmount ?? 0),
    originalSellingPricePerUnit: sellingPricePerUnitAmount,
    sellingPricePerUnit: sellingPricePerUnitAmount ?? 0,
    sellingPrice,
    originalPurchasePricePerUnit: itemData?.basePurchasePricePerUnit?.amount,
    purchasePricePerUnit: itemData?.purchasePricePerUnit?.amount,
    vatType: itemData?.sellingPriceVat?.type ?? 'S',
    discountRate: getPercentageFromDecimal(manualDiscount?.rate),
    discountType: selectedDiscount?.sourceType ?? MANUAL_DISCOUNT_SOURCE_TYPE,
    isCustomDiscount: isNotNil(selectedDiscount),
    isCustomPrice: Boolean(itemData?.isCustomPrice),
    isUnitPriceWithVat: itemData?.isUnitPriceWithVat ?? false,
    vatFreeCode: itemData?.sellingPriceVat?.freeCode ?? undefined,
    assignMechanics,
    isDoNotApplyDiscount: itemData?.isDoNotApplyDiscount ?? false,
    customPriceType: 'perUnit',
    workItemGroupId: itemData?.workItemGroupId ?? undefined,
    timeOutputPerUnit: itemData?.timeOutputPerUnit ?? undefined,
  };

  const isLoading =
    isItemLoading || isLabourRatesLoading || isItemFetching || isWorkItemGroupLoading;
  const isError = isItemError || isLabourRatesError || isWorkItemGroupError;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={143}>
      <Form<EditLabourItemFormType>
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        experimentalZodSchema={editLabourItemFormSchema}
        isDisabled={isOrderReadOnly}
      >
        {(control, formApi) => (
          <EditLabourItemFormContent
            control={control}
            formApi={formApi}
            itemData={itemData}
            labourRatesOptions={labourRatesOptions ?? []}
            labourRates={labourRates ?? []}
            workItemGroupOptions={workItemGroupOptions}
            canEditCustomPrice={canEditCustomPrice}
            canEditManualDiscount={canEditManualDiscount}
            canReadServiceOrderFinancesLabourItem={canReadServiceOrderFinancesLabourItem}
            isOrderReadOnly={isOrderReadOnly}
            isPatchLoading={isPatchLoading}
            itemId={props.itemId}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            serviceJobId={props.serviceJobId}
            onClose={props.onClose}
            formRef={props.formRef}
            data-testid={props['data-testid']}
          />
        )}
      </Form>
    </DataStatus>
  );
}
